import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Trash2 as Trash2Icon } from 'react-feather'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'

import { NoData2 } from 'components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as yup from 'yup'
import * as service from 'service'
import constants from 'constants/index'
import useStyles from './styles'

const { OPERATION_MODE_OPTIONS, HUMAN_INTERVENTION_OPTIONS } =
  constants.aiInventory

const purposeSchema = yup.object({
  description: yup.string().required().max(255),
})

const Purposes = ({
  aiInventoryId,
  isShow = false,
  purposesService = null,
  stepLabel = null,
  onHasPurposesChange,
}) => {
  const resolvedService = purposesService || service.dponet.aiInventoryPurposes
  const classes = useStyles()
  const { control, errors } = useFormContext()
  const [newPurpose, setNewPurpose] = useState('')
  const [purposeError, setPurposeError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const snackbar = useSnackbar()

  const { response, isLoading, refresh } = useFetch(
    resolvedService.get,
    aiInventoryId,
    [aiInventoryId],
    !!aiInventoryId,
  )

  const purposes = response?.data?.aiInventoryPurposes || []

  useEffect(() => {
    if (onHasPurposesChange) onHasPurposesChange(!isEmpty(purposes))
  }, [purposes.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    const description = newPurpose.trim()
    if (!description) return

    try {
      await purposeSchema.validate({ description })
      setPurposeError('')
    } catch (validationError) {
      setPurposeError(validationError.message)
      return
    }

    setIsSaving(true)
    try {
      await resolvedService.create(aiInventoryId, { description })
      setNewPurpose('')
      refresh()
    } catch {
      snackbar.open({
        message: 'Erro ao adicionar finalidade.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async (purposeId) => {
    try {
      await resolvedService.destroy(aiInventoryId, purposeId)
      refresh()
    } catch {
      snackbar.open({
        message: 'Erro ao remover finalidade.',
        variant: 'error',
      })
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAdd()
    }
  }

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="overline">
            <strong>FINALIDADE</strong>
          </Typography>
          {stepLabel && (
            <Typography variant="subtitle1">{stepLabel}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary">
          Para que? Por qual motivo a empresa utiliza IA? O objetivo é descobrir
          a finalidade.
        </Typography>
      </Box>
      <Divider />

      <Box p={2} pb={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Tipo de operação
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              A IA opera de forma:
            </Typography>
            <Controller
              name="operationMode"
              control={control}
              as={
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  disabled={isShow}
                  error={!!errors.operationMode}
                  helperText={errors.operationMode?.message}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    <Typography color="textSecondary">Selecione</Typography>
                  </MenuItem>
                  {OPERATION_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Intervenção humana obrigatória
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Há intervenção humana obrigatória nas decisões produzidas pela IA?
            </Typography>
            <Controller
              name="humanIntervention"
              control={control}
              as={
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  disabled={isShow}
                  error={!!errors.humanIntervention}
                  helperText={errors.humanIntervention?.message}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    <Typography color="textSecondary">Selecione</Typography>
                  </MenuItem>
                  {HUMAN_INTERVENTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              }
            />
          </Grid>
        </Grid>
      </Box>

      {!isShow && (
        <Box p={2} pb={0}>
          <Typography variant="subtitle2" gutterBottom>
            Por qual motivo a IA é utilizada
          </Typography>
          <TextField
            placeholder="Para criar e revisar textos, dar ideias sobre novos conteúdos e categorizar conteúdos"
            fullWidth
            variant="outlined"
            value={newPurpose}
            error={!!purposeError}
            helperText={purposeError}
            onChange={(event) => {
              setNewPurpose(event.target.value)
              if (purposeError) setPurposeError('')
            }}
            onKeyDown={handleKeyDown}
            disabled={isSaving || !aiInventoryId}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.addButton}
                    onClick={handleAdd}
                    disabled={!newPurpose.trim() || isSaving || !aiInventoryId}
                  >
                    {isSaving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Adicionar'
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      <Box p={2}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : isEmpty(purposes) ? (
          <NoData2
            title={
              isShow
                ? 'Nenhuma finalidade cadastrada.'
                : 'Adicione a primeira finalidade acima.'
            }
            withoutPaper
          />
        ) : (
          <Paper variant="outlined">
            <PerfectScrollbar
              options={{ wheelPropagation: false, useBothWheelAxes: true }}
            >
              <Table size="small" noWrap>
                <TableHead>
                  <TableRow className={classes.tableRow}>
                    <TableCell
                      className={classes.tableCell}
                      width={isShow ? '100%' : '85%'}
                    >
                      Resumo da finalidade
                    </TableCell>
                    {!isShow && (
                      <TableCell
                        className={classes.tableCell}
                        width="15%"
                        align="right"
                      >
                        Ações
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purposes.map((purpose) => (
                    <TableRow key={purpose.id} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        {purpose.description}
                      </TableCell>
                      {!isShow && (
                        <TableCell className={classes.tableCell} align="right">
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              onClick={() => handleRemove(purpose.id)}
                              className={classes.deleteButton}
                            >
                              <Trash2Icon size={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </PerfectScrollbar>
          </Paper>
        )}
      </Box>
    </Paper>
  )
}

Purposes.propTypes = {
  aiInventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isShow: PropTypes.bool,
  stepLabel: PropTypes.string,
  onHasPurposesChange: PropTypes.func,
  purposesService: PropTypes.shape({
    get: PropTypes.func,
    create: PropTypes.func,
    destroy: PropTypes.func,
  }),
}

export default Purposes
